import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Clock, Users, ShieldCheck, Check, Calendar } from "lucide-react";
import SEO from "@/components/SEO";
import ShareRow from "@/components/ShareRow";
import Countdown from "@/components/Countdown";
import { Reveal, MaskLine, SectionHead } from "@/components/Reveal";
import {
  IMAGES, AGENDA, OUTCOMES, ROOM, THEMES, MEDIA_PHASES,
  WHY_ATTEND, IMPACT_STATS, IMPACT_INSIGHTS, SUMMITS, SUMMIT_SUPPORT,
} from "@/data/content";

const GLANCE = [
  { icon: Calendar, k: "Date", v: "30 October 2026" },
  { icon: Clock, k: "Time", v: "2:00 PM – 6:00 PM · Followed by an Exclusive Dinner Buffet" },
  { icon: MapPin, k: "Venue", v: "NDMC Convention Centre, Jai Singh Road, New Delhi" },
  { icon: Users, k: "Invitees", v: "50–55 Distinguished Guests · Exclusive Invitee-Based Roundtable · Senior Media On-Site" },
  { icon: ShieldCheck, k: "Character", v: "Non-Partisan · High-Trust · Closed-Format · Cross-Sector · Inaugural Edition" },
];

const EventPage = () => (
  <>
    <SEO
      title="From Wards 2 the World — Delhi Governance Roundtable Conclave"
      path="/event"
      description="The inaugural Delhi Governance Roundtable Conclave by Smart Nagar Nigam — 30 October 2026, 2 PM – 6 PM at the NDMC Convention Centre, New Delhi, with 50–55 distinguished guests. Launch of the Good Governance Book, unveiling of Citizen Councillor Connect (C3), signing of the Delhi Ward Charter, followed by an exclusive dinner buffet."
    />
    <section className="relative flex min-h-[90vh] flex-col justify-end overflow-hidden" data-testid="event-hero">
      <div className="absolute inset-0">
        <img src={IMAGES.roundtable} alt="The Delhi Governance Roundtable Conclave — a covenant assembly in session" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/40" />
      </div>
      <div className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-14 pt-32 md:px-10 md:pb-24 md:pt-44">
        <MaskLine delay={0.1}>
          <span className="text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-gold md:text-sm">
            Delhi Governance Roundtable Conclave · Inaugural Edition
          </span>
        </MaskLine>
        <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-ivory">
          <MaskLine delay={0.25} className="text-[12vw] md:text-[8vw]">From Wards</MaskLine>
          <MaskLine delay={0.4} className="text-[12vw] md:text-[8vw]">
            <span className="text-outline">2 the World.</span>
          </MaskLine>
        </h1>
        <MaskLine delay={0.55}>
          <p className="mt-8 max-w-2xl font-display text-xl font-bold leading-snug text-ivory md:text-2xl">
            Building the World's Best Capital, One Ward at a Time.
          </p>
        </MaskLine>
        <MaskLine delay={0.65}>
          <p className="mt-4 max-w-2xl text-sm uppercase tracking-[0.2em] text-white/60 md:text-base">
            Theme — Transforming Delhi Through Data, Leadership & Citizen Partnership
          </p>
        </MaskLine>
        <MaskLine delay={0.75}>
          <p className="mt-8 font-display text-lg font-bold uppercase tracking-[0.12em] text-gold md:text-2xl" data-testid="event-hero-dateline">
            30 October 2026 · 2 PM – 6 PM · NDMC Convention Centre, New Delhi
          </p>
        </MaskLine>
        <MaskLine delay={0.85}>
          <p className="mt-3 text-xs uppercase tracking-[0.25em] text-white/60 md:text-sm">
            Followed by an Exclusive Dinner Buffet
          </p>
        </MaskLine>
      </div>
    </section>

    <section className="border-t border-white/10 bg-coal" data-testid="event-countdown-strip">
      <div className="mx-auto max-w-[1600px] px-5 py-14 md:px-10 md:py-20">
        <Reveal>
          <Countdown />
        </Reveal>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="event-glance">
      <div className="mx-auto grid max-w-[1600px] gap-px border-x border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
        {GLANCE.map((g, i) => (
          <Reveal key={g.k} delay={i * 0.06} className="bg-obsidian p-8 md:p-10">
            <g.icon size={22} className="text-gold" aria-hidden="true" />
            <p className="mt-5 text-[11px] uppercase tracking-[0.25em] text-white/40">{g.k}</p>
            <p className="mt-3 text-sm leading-relaxed text-white/75">{g.v}</p>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="event-overview">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="01" overline="An Overview" title="The Conclave, in Full Context." />
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="space-y-6 text-base leading-relaxed text-white/70 md:text-lg">
            <p>
              From Wards 2 the World — the Delhi Governance Roundtable Conclave — is the
              inaugural edition of a national programme by Smart Nagar Nigam. An exclusive,
              non-partisan, high-trust, closed-format roundtable, it convenes 50–55
              distinguished guests at the NDMC Convention Centre, New Delhi, on
              30 October 2026 — 2 PM to 6 PM, followed by an exclusive dinner buffet.
            </p>
            <p>
              Under the theme "Transforming Delhi Through Data, Leadership & Citizen
              Partnership," the Conclave launches the Good Governance Book, unveils Citizen
              Councillor Connect (C3) and the Digital Public Representative, showcases startup
              and innovation solutions across 30 MCD domains, and signs the Delhi Ward Charter
              — a public governance covenant.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="space-y-6 text-base leading-relaxed text-white/70 md:text-lg">
            <p>
              The gathering brings together political leadership across party lines, senior
              bureaucracy, incumbent and aspiring councillors, think tanks, academia, civil
              society, industry, startups, and media — each seat calibrated for credibility,
              balance, and actionable follow-through.
            </p>
            <p className="border-l-2 border-gold pl-6 font-display text-xl font-bold leading-snug text-ivory md:text-2xl">
              Delhi is Edition One. The framework travels next to Mumbai and Bareilly — and
              ultimately to India's 4,000+ Urban Local Bodies.
            </p>
            <Link to="/initiatives" data-testid="event-overview-initiatives-link" className="inline-flex items-center gap-3 font-display text-sm font-bold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:text-gold-light">
              The Initiatives Behind It <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <figure className="mt-14 border border-white/10" data-testid="event-mandate-poster">
            <img
              src={IMAGES.posterMandate}
              alt="From Wards 2 the World — The Constitutional Mandate: A Roundtable on the Future of Democratic Urban Governance. 30 October 2026, 2 PM – 6 PM, NDMC Convention Centre, New Delhi"
              className="w-full object-cover"
              loading="lazy"
            />
          </figure>
        </Reveal>
      </div>
    </section>

    <section className="section-light border-t border-white/10" data-testid="event-outcomes">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="02" overline="Five Founding Outcomes" title="What This Room Will Set in Motion." />
        <div className="border border-white/10">
          {OUTCOMES.map((o, i) => (
            <Reveal key={o} delay={i * 0.04}>
              <div className="flex items-start gap-6 border-b border-white/10 p-6 last:border-b-0 md:p-8">
                <span className="font-display text-2xl font-extrabold tracking-tight text-gold md:text-3xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-base leading-relaxed text-white/75 md:text-lg">{o}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="event-why-attend">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="03" overline="Why Attend" title="A Compelling Value Proposition for Every Stakeholder." />
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
          {WHY_ATTEND.map((w, i) => (
            <Reveal key={w.group} delay={i * 0.06} className="bg-obsidian p-8 md:p-12">
              <span className="font-display text-sm font-bold tracking-[0.3em] text-gold">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-ivory">{w.group}</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/60 md:text-base">{w.intro}</p>
              <ul className="mt-6 space-y-3">
                {w.points.map((p) => (
                  <li key={p} className="flex gap-3 text-sm leading-relaxed text-white/70">
                    <Check size={16} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="event-agenda">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="04" overline="The Detailed Agenda" title="One Afternoon That Redirects a Capital." />
        <div className="border border-white/10">
          {AGENDA.map((a, i) => (
            <Reveal key={a.time} delay={i * 0.02}>
              <div className="grid gap-3 border-b border-white/10 p-6 last:border-b-0 md:grid-cols-12 md:items-baseline md:p-8">
                <p className="font-display text-lg font-bold tracking-tight text-gold md:col-span-3">{a.time}</p>
                <div className="md:col-span-6">
                  <h3 className="font-display text-xl font-bold text-ivory">{a.segment}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{a.detail}</p>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-white/40 md:col-span-3 md:text-right">{a.lead}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/10 bg-coal" data-testid="event-room">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="05" overline="Who Should Attend" title="Not an Audience. A Covenant Assembly." />
        <p className="mb-12 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
          The power of this Conclave lies entirely in who is in the room. Every seat has been
          calibrated for cross-sector credibility, political balance, innovation energy and
          actionable follow-through — fifty to fifty-five distinguished voices, each chosen
          for the specific role they must play in Delhi's governance ecosystem.
        </p>
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
          {ROOM.map((r, i) => (
            <Reveal key={r.n} delay={(i % 4) * 0.04} className="bg-coal p-6 md:p-8">
              <div className="flex items-baseline gap-4">
                <span className="font-display text-sm font-bold tracking-[0.25em] text-gold">{r.n}</span>
                <h3 className="font-display text-lg font-bold leading-snug text-ivory">{r.cat}</h3>
              </div>
              <p className="mt-3 text-sm font-medium text-white/70">{r.who}</p>
              <p className="mt-2 text-xs leading-relaxed text-white/50">{r.why}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="event-themes">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="06" overline="Intellectual Architecture" title="Five Key Themes." />
        <div className="grid gap-12">
          {THEMES.map((t, i) => (
            <Reveal key={t.n} delay={0.05}>
              <div className="grid gap-4 border-t border-white/10 pt-8 md:grid-cols-12">
                <span className="font-display text-4xl font-extrabold tracking-tight text-gold md:col-span-2 md:text-5xl">{t.n}</span>
                <h3 className="font-display text-2xl font-bold tracking-tight text-ivory md:col-span-4 md:text-3xl">{t.title}</h3>
                <p className="text-base leading-relaxed text-white/65 md:col-span-6">{t.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="section-light border-t border-white/10" data-testid="event-impact">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="07" overline="Impact & Insights" title="The Ripple This Room Creates." />
        <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-3 lg:grid-cols-6">
          {IMPACT_STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.05} className="bg-coal p-8">
              <p className="font-display text-4xl font-extrabold tracking-tight text-gold md:text-5xl">{s.value}</p>
              <p className="mt-3 text-xs uppercase tracking-[0.18em] text-white/50">{s.label}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 grid gap-px border border-white/10 bg-white/10 lg:grid-cols-3">
          {IMPACT_INSIGHTS.map((ins, i) => (
            <Reveal key={ins.title} delay={i * 0.06} className="bg-obsidian p-8 md:p-10">
              <h3 className="font-display text-xl font-bold tracking-tight text-ivory md:text-2xl">{ins.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/60">{ins.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="event-summits">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="08" overline="From Wards 2 the World — Zonal Ward Summit Townhalls" title="The Conclave Is the Spark. The Summits Are the Fire." />
        <p className="mb-12 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
          The Conclave creates the platform. But accountability lives where citizens live.
          Following the Conclave, Smart Nagar Nigam executes four Ward Summit townhall events
          across Delhi's four zones — bringing 300–500 citizens face to face with their ward
          councillor, with live C3 demonstrations, grievance registration stations, and full
          media coverage.
        </p>
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
          {SUMMITS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05} className="bg-obsidian p-8">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold">{s.n}</p>
              <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-ivory">{s.zone}</h3>
              <p className="mt-3 text-sm font-medium text-white/70">{s.theme}</p>
              <p className="mt-3 border-t border-white/10 pt-3 text-xs leading-relaxed text-white/50">{s.format}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 grid gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.25em] text-gold">What Smart Nagar Nigam Provides at Every Summit</p>
            <ul className="mt-6 space-y-3">
              {SUMMIT_SUPPORT.map((s) => (
                <li key={s} className="flex gap-3 text-sm leading-relaxed text-white/70">
                  <Check size={16} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                  {s}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-gold/30 bg-coal p-8 md:p-10">
              <p className="font-display text-xl font-bold leading-snug text-ivory md:text-2xl">
                At each Summit, the ward councillor presents their Ward Vision Document,
                answers citizen questions in a structured format, and commits to measurable
                outcomes — with Smart Nagar Nigam publishing the verified record.
              </p>
              <p className="mt-6 text-sm leading-relaxed text-white/60">
                Post-event media packages reach 150+ platforms within 72 hours. Ward Summits
                run in sustained waves, keeping accountability visible between election cycles.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="border-t border-white/10 bg-coal" data-testid="event-media">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="09" overline="Media Architecture" title="Before, During & After — A 150+ Platform Wave." />
        <div className="grid gap-px border border-white/10 bg-white/10 lg:grid-cols-3">
          {MEDIA_PHASES.map((m, i) => (
            <Reveal key={m.phase} delay={i * 0.06} className="bg-coal p-8 md:p-10">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold">{m.when}</p>
              <h3 className="mt-3 font-display text-xl font-bold text-ivory">{m.phase}</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/60">{m.actions}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="event-venue-map">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="10" overline="Date, Time & Venue" title="NDMC Convention Centre, New Delhi." />
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <dl className="border border-white/10">
              {[
                ["Date", "Friday, 30 October 2026"],
                ["Time", "2:00 PM – 6:00 PM (Registration & Hi-Tea from 1:30 PM)"],
                ["Venue", "NDMC Convention Centre, Jai Singh Road, New Delhi"],
                ["After the Conclave", "An Exclusive Dinner Buffet for all distinguished guests"],
              ].map(([k, v]) => (
                <div key={k} className="border-b border-white/10 p-6 last:border-b-0">
                  <dt className="text-[11px] uppercase tracking-[0.25em] text-gold">{k}</dt>
                  <dd className="mt-2 text-base font-medium leading-relaxed text-white/80">{v}</dd>
                </div>
              ))}
            </dl>
            <a
              href="https://www.google.com/maps/search/?api=1&query=NDMC+Convention+Centre,+Jai+Singh+Road,+New+Delhi"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="venue-directions-link"
              className="mt-8 inline-flex items-center gap-3 border border-white/20 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <MapPin size={16} /> Get Directions on Google Maps
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden border border-white/10">
              <iframe
                title="NDMC Convention Centre, New Delhi — Google Map"
                src="https://www.google.com/maps?q=NDMC%20Convention%20Centre%2C%20Jai%20Singh%20Road%2C%20New%20Delhi&output=embed"
                className="h-[420px] w-full"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="venue-map-iframe"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="border-t border-gold/30 bg-gold" data-testid="event-cta">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-28">
        <Reveal>
          <p className="max-w-4xl font-display text-3xl font-extrabold leading-[1.05] tracking-tight text-obsidian sm:text-4xl lg:text-5xl">
            "This is not a conference. It is a covenant. Every person in this room will be
            remembered as someone who chose governance over politics, accountability over
            convenience, and Delhi's citizens over partisan advantage."
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap items-center gap-8">
            <Link to="/register" data-testid="event-register-cta" className="inline-flex items-center gap-3 bg-obsidian px-10 py-5 font-display text-sm font-bold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:bg-coal">
              Request Your Seat <ArrowRight size={16} />
            </Link>
            <ShareRow title="From Wards 2 the World — Delhi Governance Roundtable Conclave" path="/event" />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default EventPage;
